import { SimProductTagProvider } from '../Tags/SimProductTagProvider';
import { SuspenseLoadingView } from 'common/components/SuspenseLoadingView';
import Box from '@mui/material/Box';
import { DetailsSkeleton } from 'common/components/DetailsSkeleton';
import Stack from '@mui/material/Stack';
import { useParams } from 'react-router';
import { SimDetails } from 'common/components/Sim/Details/SimDetails';
import { useEditAvailable } from '../Edit/hooks/useEditAvailable';
import { SimProductEdit } from './SimProductDetailsEdit';
import { Routes } from 'src/Routes.constants';
import { styled } from '@mui/material/styles';
import { simProductQueries } from './queries/simProductsQueries';
import { RefetchQueryButton } from 'src/common/components/Queries/RefetchQueryButton';
import { ScrollArea } from 'src/common/components/ScrollArea/ScrollArea';
import { useSimProductDetails } from './hooks/useSimProductDetails';
import { TabPanelWithErrorHandling } from 'src/common/components/AppLayout/Components/TabPanelWithErrorHandling';
import { ErrorCodes } from 'src/common/responseCodes';
import { useTranslation } from 'react-i18next';
import { detailsQueries } from 'src/common/components/Sim/Details/queries/detailsQueries';
import {
  enrichWithSessionDetailsMapper,
  useSessionsDetails,
} from 'src/simInventory/hooks/useSessionsDetails';
import { useHasFeatureFlag } from 'src/featureFlags/useHasFeatureFlag';

export type SimInventoryEntryRoutingParams = {
  iccid: string;
};

const SimProductDetailsContentColumn = styled(Stack)({
  padding: '8px 0',
  height: '100%',
});

const IGNORED_ERRORS = [ErrorCodes.NotFound];

const SimProductDetailsContent = () => {
  const { iccid } = useParams<SimInventoryEntryRoutingParams>();
  if (!iccid) {
    throw new Error('No iccid');
  }

  const { data } = useSimProductDetails(iccid);

  const displaySimEditing = useEditAvailable();

  const { data: sessionsDetails, isFetching: isSessionDetailsLoading } = useSessionsDetails({
    imsis: data?.imsi ? [data.imsi] : [],
    queryKey: detailsQueries.sessionDetails(data?.imsi),
  });

  if (data === null) {
    throw new Error('No data for iccid');
  }
  const isRadiusInterimsFilteringEnabled = useHasFeatureFlag('RadiusInterimsFiltering');
  const enrichedData = isRadiusInterimsFilteringEnabled
    ? enrichWithSessionDetailsMapper(data, sessionsDetails)
    : data;

  return (
    <SimProductDetailsContentColumn>
      <Box
        flex={0}
        sx={{
          display: 'flex',
          justifyContent: 'end',
          pt: 8,
          pr: 24,
        }}
      >
        <RefetchQueryButton queryKey={detailsQueries.details} />
        {displaySimEditing && <SimProductEdit iccid={iccid} />}
      </Box>
      <ScrollArea className="os-host-flexbox">
        <SimDetails
          data={enrichedData}
          baseRoute={Routes.productsSim}
          isSessionDetailsLoading={isSessionDetailsLoading}
        />
      </ScrollArea>
    </SimProductDetailsContentColumn>
  );
};

export const SimProductDetails = () => {
  const { t } = useTranslation();
  const { iccid } = useParams<SimInventoryEntryRoutingParams>();

  return (
    <TabPanelWithErrorHandling
      name={t('products.tabs.general')}
      ignoreErrorMessages={IGNORED_ERRORS}
    >
      <SimProductTagProvider>
        <SuspenseLoadingView
          queryKey={simProductQueries.detail({ iccid: iccid! })}
          loadFallback={
            <Box
              sx={{
                pt: 12,
                pb: 24,
                px: 64,
                mt: 44,
              }}
            >
              <DetailsSkeleton numberOfRepetitions={8} />
            </Box>
          }
        >
          <SimProductDetailsContent />
        </SuspenseLoadingView>
      </SimProductTagProvider>
    </TabPanelWithErrorHandling>
  );
};
